import { MutationFunctionOptions } from '@apollo/client';

import { SubmitExpenseClaimRequestInput } from '__generated__/GQL';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useErrorHandledMutation from 'common/hooks/useErrorHandledMutation';
import useMainBankAccountId from 'common/hooks/useMainBankAccountId';
import { UserBankDetailsFormSchema } from 'components/UserBankDetailsForm/schema';

import { tabFilters } from '../../pages/Home/filters';
import { ReceiptInformationFormSchema } from '../../pages/New/components/NewExpenseClaimRequestStepper/steps/ReceiptInformation/components/ReceiptInformationForm/schema';
import { ManagedUserExpenseClaimRequestsDocument } from '../useManagedUserExpenseClaimRequests/managedUserExpenseClaimRequests.gql';
import {
  SubmitExpenseClaimRequestDocument,
  SubmitExpenseClaimRequestMutation,
  SubmitExpenseClaimRequestMutationVariables,
} from './submitExpenseClaimRequest.gql';

type Options = Omit<
  MutationFunctionOptions<
    SubmitExpenseClaimRequestMutation,
    SubmitExpenseClaimRequestMutationVariables
  >,
  'variables'
>;

const mapExpenseClaimRequestFormValuesToInput = (
  values: UserBankDetailsFormSchema & ReceiptInformationFormSchema,
): Omit<
  SubmitExpenseClaimRequestInput,
  'bankAccountId' | 'companyProfileId'
> => ({
  amount: {
    total: Math.round(values.amount.total * 100),
    vatItems: values.amount.vatItems.map((vatItem) => ({
      amount: Math.round(vatItem.amount * 100),
      rate: Number((vatItem.rate / 100).toFixed(3)),
    })),
  },
  bankTransferRecipient: {
    iban: values.iban,
    name: values.name,
    swiftBic: values.swiftBic,
  },
  description: values.description,
  reseller: values.reseller,
  supportingDocument: {
    bucketName: values.supportingDocument.bucketName,
    fileRef: values.supportingDocument.fileRef,
  },
});

export const useSubmitExpenseClaimRequest = (options?: Options) => {
  const { mainBankAccountId } = useMainBankAccountId();
  const { companyProfileId, companyUserId } = useCompanyContext();

  const [mutation, hooks] = useErrorHandledMutation<
    SubmitExpenseClaimRequestMutation,
    SubmitExpenseClaimRequestMutationVariables
  >(SubmitExpenseClaimRequestDocument, options);

  const submitExpenseClaimRequest = (
    input: UserBankDetailsFormSchema & ReceiptInformationFormSchema,
    mutationOptions?: Options,
  ) =>
    mutation({
      ...mutationOptions,
      refetchQueries: [
        {
          query: ManagedUserExpenseClaimRequestsDocument,
          variables: {
            after: null,
            companyProfileId: companyProfileId ?? '',
            requesterCompanyId: companyUserId ?? '',
            status: tabFilters['pending'],
          },
        },
      ],
      variables: {
        input: {
          bankAccountId: mainBankAccountId ?? '',
          companyProfileId: companyProfileId ?? '',
          ...mapExpenseClaimRequestFormValuesToInput(input),
        },
      },
    });

  return [submitExpenseClaimRequest, hooks] as const;
};
